import type { SxProps, Theme } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';

/** Field of the row-drag grip pseudo column injected by index.tsx. */
export const ROW_DRAG_FIELD = '__hfRowDrag';

/** Width of the row-drag grip column (matches the `__hfRowDrag` colDef in index.tsx). */
export const ROW_DRAG_COLUMN_PX = 36;

/** Field of the expand-toggle pseudo column injected when `rowTree` is set. */
export const ROW_EXPAND_FIELD = '__hfRowExpand';

/** Width of the expand column before same-column depth indent (22px button plus cell padding). */
export const ROW_EXPAND_COLUMN_PX = 40;

/** Extra left inset per tree depth in same-column mode. */
export const ROW_TREE_INDENT_PX = 16;

/**
 * Themed scrollbars (WebKit + Firefox) for any overflow container.
 * Spread it onto the scrolling element so HF scrollers match the grid.
 * Colors come from the `--hf-scrollbar-*` variables published by `createHfTheme`'s
 * CssBaseline, so grid bars stay in sync with the global (native) ones.
 */
export const HF_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'var(--hf-scrollbar-thumb) var(--hf-scrollbar-track)',
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'var(--hf-scrollbar-track)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--hf-scrollbar-thumb)',
    borderRadius: 1,
    border: '2px solid transparent',
    backgroundClip: 'content-box',
    '&:hover': { backgroundColor: 'var(--hf-scrollbar-thumb-hover)' },
  },
} satisfies SxProps<Theme>;

/** Base grid styling: borders, scrollbars, header typography, row washes, sign colors. */
export const HF_DATA_TABLE_SX: SxProps<Theme> = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  fontVariantNumeric: 'tabular-nums',
  '--DataGrid-rowBorderColor': 'transparent',
  // DataGrid already draws an overlay `.MuiDataGrid-scrollbar`. Styling the
  // virtual scroller's native bar (or the global `*` scrollbar CSS) un-hides it
  // and you get two thumbs. Keep native hidden; theme only the overlay.
  '& .MuiDataGrid-virtualScroller': {
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  '& .MuiDataGrid-scrollbar': HF_SCROLLBAR_SX,
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 800,
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'text.secondary',
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    overflow: 'visible',
    minWidth: 0,
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    overflow: 'visible',
  },
  '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
    borderBottom: '1px solid',
    borderColor: 'divider',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  // The last header's resize separator overhangs the grid edge by a few px,
  // forcing a micro horizontal scroll when columns exactly fill the viewport.
  '& .MuiDataGrid-columnHeader--last': {
    overflow: 'hidden',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderColor: 'divider',
    fontSize: 13,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  // Zebra stays subtle; hover is a separate primary wash; selection is stronger primary tint.
  // Each state publishes its wash as `--hf-row-bg` so sticky cells (opaque so
  // scrolled cells don't bleed through — see stickyLeftSx) can tint to match the row.
  '& .MuiDataGrid-row:nth-of-type(even)': {
    '--hf-row-bg': (theme) => theme.palette.action.hover,
    bgcolor: 'var(--hf-row-bg)',
  },
  '& .MuiDataGrid-row:hover': {
    '--hf-row-bg': (theme) => theme.alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06),
    bgcolor: 'var(--hf-row-bg)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    '--hf-row-bg': (theme) => theme.alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.14),
    bgcolor: 'var(--hf-row-bg)',
    boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
    '&:hover': {
      '--hf-row-bg': (theme) => theme.alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.36 : 0.2),
      bgcolor: 'var(--hf-row-bg)',
    },
  },
  '& .hf-cell-pos': {
    color: 'success.main',
    fontWeight: 650,
  },
  '& .hf-cell-neg': {
    color: 'error.main',
    fontWeight: 650,
  },
  '& .hf-cell-blank': {
    color: 'text.secondary',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderColor: 'divider',
    minHeight: 44,
  },
  '& .MuiDataGrid-columnSeparator': {
    color: 'divider',
  },
  '& .MuiDataGrid-iconButtonContainer': {
    visibility: 'visible',
    width: 18,
    '& .MuiIconButton-root': {
      padding: 0,
      width: 18,
      height: 18,
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: 'action.hover' },
    },
  },
  '& .MuiDataGrid-sortIcon': {
    fontSize: 14,
    opacity: 0.7,
  },
  '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
    opacity: 1,
  },
  '& .MuiDataGrid-menuIcon': {
    width: 20,
    '& .MuiIconButton-root': {
      padding: 0,
      width: 18,
      height: 18,
      backgroundColor: 'transparent',
    },
  },
  '& .MuiDataGrid-toolbar': {
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
};

/**
 * Header z-indexes sit above the grid's column separators (z-index 30), which
 * would otherwise paint through the pinned headers while scrolling X.
 * Cell z-indexes only need to beat unpinned cells (auto).
 */
const HEADER_Z = 31;
const CELL_Z = 2;

export type StickyLeftOptions = {
  checkbox?: boolean;
  rowDrag?: boolean;
  /**
   * How many leading data columns to pin (after checkbox / row-drag).
   * Default 1. When > 1, `dataColumnWidth` is required so `left` offsets accumulate.
   */
  dataColumnCount?: number;
  /** Fixed width of each pinned data column when `dataColumnCount` > 1. */
  dataColumnWidth?: number;
  /** Width of the expand pseudo column, pinned after checkbox and row-drag. */
  expandWidth?: number;
};

/**
 * Sticky body cells need an opaque base (scrolled cells slide beneath them),
 * which would hide the row's zebra / hover / selection washes. The row styles
 * in HF_DATA_TABLE_SX publish the active wash as `--hf-row-bg`, and sticky
 * cells re-apply it over the opaque base so they always match the rest of the row.
 */
const ROW_WASH_OVERLAY = 'linear-gradient(var(--hf-row-bg, transparent), var(--hf-row-bg, transparent))';

/**
 * Stick the leading columns: checkbox, row-drag grip (when present), and one or
 * more data columns. Each gets a `left` equal to the widths of the columns pinned
 * before it, and an opaque `background.paper` so scrolled cells don't bleed through
 * (body cells tint it with the row wash — see `ROW_WASH_OVERLAY`).
 * The last pinned column draws a divider on its right edge (inset shadow, so
 * column widths are untouched) to mark the sticky/scrolled boundary.
 * `aria-colindex` is 1-based over all columns, so it identifies pinned data
 * columns regardless of scroll position (index.tsx disables column
 * virtualization in sticky mode so the pinned cells stay mounted).
 */
export function stickyLeftSx(opts: StickyLeftOptions = {}): SxProps<Theme> {
  const header = (left: number, zIndex: number) => ({
    position: 'sticky',
    left,
    zIndex,
    bgcolor: 'background.paper',
    backgroundImage: 'none',
  });
  const cell = (left: number, zIndex: number) => ({
    position: 'sticky',
    left,
    zIndex,
    bgcolor: 'background.paper',
    backgroundImage: ROW_WASH_OVERLAY,
  });

  const sx: Record<string, object> = {};
  const cellSelectors: string[] = [];
  let left = 0;
  let colIndex = 1;

  if (opts.checkbox) {
    sx['& .MuiDataGrid-columnHeaderCheckbox'] = header(left, HEADER_Z + 1);
    sx['& .MuiDataGrid-cellCheckbox'] = cell(left, CELL_Z + 1);
    cellSelectors.push('.MuiDataGrid-cellCheckbox');
    left += GRID_CHECKBOX_SELECTION_COL_DEF.width ?? 50;
    colIndex += 1;
  }

  if (opts.rowDrag) {
    sx[`& .MuiDataGrid-columnHeader[aria-colindex="${colIndex}"]`] = header(left, HEADER_Z + 1);
    sx[`& .MuiDataGrid-cell[aria-colindex="${colIndex}"]`] = cell(left, CELL_Z + 1);
    cellSelectors.push(`.MuiDataGrid-cell[aria-colindex="${colIndex}"]`);
    left += ROW_DRAG_COLUMN_PX;
    colIndex += 1;
  }

  if (opts.expandWidth) {
    sx[`& .MuiDataGrid-columnHeader[aria-colindex="${colIndex}"]`] = header(left, HEADER_Z + 1);
    sx[`& .MuiDataGrid-cell[aria-colindex="${colIndex}"]`] = cell(left, CELL_Z + 1);
    cellSelectors.push(`.MuiDataGrid-cell[aria-colindex="${colIndex}"]`);
    left += opts.expandWidth;
    colIndex += 1;
  }

  const dataCount = Math.max(1, opts.dataColumnCount ?? 1);
  const dataWidth = opts.dataColumnWidth ?? 0;
  const edge = {
    boxShadow: (theme: Theme) => `inset -1px 0 0 ${theme.palette.divider}`,
  };

  for (let i = 0; i < dataCount; i += 1) {
    const isLast = i === dataCount - 1;
    const headerRule = isLast ? { ...header(left, HEADER_Z), ...edge } : header(left, HEADER_Z);
    const cellRule = isLast ? { ...cell(left, CELL_Z), ...edge } : cell(left, CELL_Z);
    sx[`& .MuiDataGrid-columnHeader[aria-colindex="${colIndex}"]`] = headerRule;
    const cellSel = `.MuiDataGrid-cell[aria-colindex="${colIndex}"]`;
    sx[`& ${cellSel}`] = cellRule;
    cellSelectors.push(cellSel);
    if (!isLast) {
      // Multi-pin needs a known width so the next column's `left` accumulates.
      left += dataWidth;
    }
    colIndex += 1;
  }

  // The selected row's accent bar (inset 3px, see HF_DATA_TABLE_SX) paints under
  // the opaque first sticky cell — repaint it there, merged with the edge divider
  // when the first sticky cell is also the last.
  const firstCell = cellSelectors[0];
  const firstIsLast = cellSelectors.length === 1;
  sx[`& .MuiDataGrid-row.Mui-selected ${firstCell}`] = {
    boxShadow: (theme: Theme) => `inset 3px 0 0 ${theme.palette.primary.main}${firstIsLast ? `, inset -1px 0 0 ${theme.palette.divider}` : ''}`,
  };

  return sx;
}
